import React, { useState, useEffect } from "react";
import { useKitBalance, useKitTransactions, useWithdrawKit, useExportTransactions } from "../hooks/useDashboard.ts";
import { useMembers } from "../hooks/useMembers.ts";
import { useDebounce } from "../hooks/useDebounce.ts";
import { Wallet, Search, DollarSign, Download, Trash2 } from "lucide-react";
import PhoneFilters from "../components/PhoneFilters";
import Table from "../components/Table.tsx";
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useMemberContributions, useMemberContributionStats } from '../hooks/useContributions.ts';
import Select from '../components/ui/Select.tsx';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

export default function TransactionsPage() {
  const { can } = usePermissions();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('group_kit'); // Default to 'group_kit', will be updated when profile loads
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const isAdmin = useIsAdmin();
  const { data: profile } = useGetProfile();
  
  // Privacy settings
  const memberVisibilityEnabled = profile?.data?.current_group?.can_view_other_members ?? false;
  const canViewOthers = isAdmin || memberVisibilityEnabled;
  const isHouseManagement = Number(profile?.data?.current_group?.is_house_management) === 1;
  
  // Debounce search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  const perPage = 50;
  const { data: kitBalance } = useKitBalance();
  const { data: kitTransactions, isLoading } = useKitTransactions({ 
    page, 
    per_page: perPage,
    search: debouncedSearchQuery,
    member_id: selectedMember?.value,
    date_from: dateFrom,
    date_to: dateTo,
  });
  const { data: membersData } = useMembers();
  
  // Member-specific data
  const { data: memberContributionStats } = useMemberContributionStats();
  const { data: memberContributions, isLoading: isLoadingMemberContributions } = useMemberContributions({
    per_page: 10,
    page: 1
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newBalance, setNewBalance] = useState(kitBalance?.data?.balance ? Number(kitBalance.data.balance) : '');
  const [remarks, setRemarks] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  
  const [syncTx, setSyncTx] = useState(null); // Holds the transaction being allocated

  const queryClient = useQueryClient();
  const withdrawKit = useWithdrawKit();
  const exportTransactions = useExportTransactions();

  // Delete transaction mutation
  const deleteTransaction = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/kit/transactions/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Transaction deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['kit-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] });
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to delete transaction');
      setDeleteConfirmId(null);
    }
  });

  // Allocate transaction mutation
  const allocateMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await api.post(`/kit/transactions/${payload.transaction_id}/allocate`, {
        member_id: payload.member_id
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Transaction allocated successfully');
      queryClient.invalidateQueries({ queryKey: ['kit-transactions'] });
      queryClient.refetchQueries({ queryKey: ['kit-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] });
      queryClient.refetchQueries({ queryKey: ['kit-balance'] });
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['member-contributions'] });
      queryClient.invalidateQueries({ queryKey: ['member-stats'] });
      setSyncTx(null);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to allocate transaction');
    }
  });

  const handleWithdraw = () => {
    withdrawKit.mutate({
      amount: newBalance,
      remarks: remarks,
    }, {
      onSuccess: () => {
        setIsModalOpen(false);
        setNewBalance('');
        setRemarks('');
      }
    });

  };

  // Handle search and filter changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleMemberChange = (option) => {
    setSelectedMember(option);
    setPage(1); // Reset to first page when filtering
  };

  // Handle export
  const handleExport = (format) => {
    exportTransactions.mutate({
      search: debouncedSearchQuery,
      member_id: selectedMember?.value,
      format: format,
    }, {
      onSuccess: () => {
        toast.success(`Transactions exported successfully in ${format.toUpperCase()} format!`);
        setShowExportModal(false);
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to export transactions');
      }
    });
  };

  // Prepare member options for select
  const memberOptions = membersData?.data?.map(member => ({
    value: member.id,
    label: `${member.name} (${member.phone_number})`
  })) || [];

  // Get current balance based on filter
  const getCurrentBalance = () => {
    return 'Group Balance';
  };

  // Define columns for the Table
  const kitTransactionsColumns = [
    {
      label: 'Name',
      field: 'name',
      render: (tx) => tx.formatted_transaction?.formatted_name,
    },
    {
      label: 'Amount',
      field: 'amount',
      render: (tx) => {
        const isAddition = tx.formatted_transaction?.is_addition;
        const amount = tx.formatted_transaction?.formatted_amount;
        return (
          <span className={`font-bold ${isAddition ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {amount}
          </span>
        );
      },
    },
    {
      label: 'Date',
      field: 'transaction_date',
      render: (tx) => tx.formatted_transaction?.formatted_date,
    },
    {
      label: 'Account No',
      field: 'phone',
      width: '50px',
      render: (tx) => tx.formatted_transaction?.formatted_account_no,
    },
    {
      label: 'Reference',
      field: 'transaction_id',
      render: (tx) => tx.formatted_transaction?.formatted_reference,
    },
    {
      label: 'Allocation Status',
      field: 'allocation_status',
      render: (tx) => {
        const status = tx.formatted_transaction?.allocation_status || '';
        if (!status || status === '-') return '-';

        let colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        
        if (status.includes('Allocated to:')) {
            colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
        } else if (status.includes('Floating (Matches:')) {
            colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
        } else if (status.includes('Floating (Unknown)')) {
            colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
        }

        const suggestedMemberId = tx.formatted_transaction?.suggested_member_id || tx.suggested_member_id;

        return (
          <div className="flex items-center space-x-2">
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
              {status}
            </span>
            {status.includes('Floating (Matches:') && suggestedMemberId && (
              <button
                onClick={() => setSyncTx(tx)}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded border border-blue-200 font-medium transition-colors dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
              >
                Sync
              </button>
            )}
          </div>
        );
      }
    },
    {
      label: 'Remarks',
      field: 'remarks',
      render: (tx) => {
        const remarks = tx.formatted_transaction?.remarks;
        if (!remarks || remarks === '-') return '-';
        const maxLength = 30;
        const truncated = remarks.length > maxLength ? remarks.substring(0, maxLength) + '...' : remarks;
        return (
          <span title={remarks} className="cursor-help">
            {truncated}
          </span>
        );
      },
    },
    {
      label: 'Status',
      field: 'status',
      render: (tx) => {
          const status = tx.formatted_transaction?.formatted_status || tx.status;
          return (
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
            status?.toLowerCase() === 'completed' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {status}
          </span>
          );
      },
    },
    {
      label: 'Previous Balance',
      field: 'previous_balance',
      render: (tx) => tx.raw_payload?.previous_balance ? `KSh ${Number(tx.raw_payload.previous_balance).toLocaleString()}` : '-',
    },
    {
      label: 'New Balance',
      field: 'new_balance',
      render: (tx) => tx.raw_payload?.new_balance ? `KSh ${Number(tx.raw_payload.new_balance).toLocaleString()}` : '-',
    },
    ...(isAdmin && can(PERMISSIONS.VIEW_TRANSACTIONS) ? [{
      label: 'Actions',
      field: 'actions',
      render: (tx) => {
        if (!can(PERMISSIONS.VIEW_TRANSACTIONS)) return null;
        
        return (
          <div className="flex items-center gap-2">
            {deleteConfirmId === tx.id ? (
              <>
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete this transaction?\n\nAmount: KSh ${Number(tx.amount).toLocaleString()}\nType: ${tx.type}\nReference: ${tx.transaction_id || tx.uuid}\n\nThis action cannot be undone.`)) {
                      deleteTransaction.mutate(tx.id);
                    } else {
                      setDeleteConfirmId(null);
                    }
                  }}
                  disabled={deleteTransaction.isPending}
                  className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteTransaction.isPending ? 'Deleting...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={deleteTransaction.isPending}
                  className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setDeleteConfirmId(tx.id)}
                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                title="Delete transaction"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      },
    }] : []),
  ];

  const [showQuickAccessModal, setShowQuickAccessModal] = useState(false);

  // Define columns for member contributions table
  const memberContributionsColumns = [
    {
      label: 'Amount',
      field: 'amount',
      render: (contribution) => (
        <span className="font-bold text-green-600 dark:text-green-400">
          KSh {Number(contribution.amount).toLocaleString()}
        </span>
      ),
    },
    {
      label: 'Date',
      field: 'contribution_date',
      render: (contribution) => new Date(contribution.contribution_date).toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      }),
    },
    {
      label: 'Notes',
      field: 'notes',
      render: (contribution) => contribution.notes || '-',
    }
  ];

  return (
    <div className="px-1 py-4 min-h-screen md:px-0 mb-15">
      <div className="flex flex-col gap-4">
        {/* Balance Cards */}
        {!isAdmin ? (
          // Member Dashboard - Show member balance only
          <div className="flex flex-col p-4 bg-card-bg rounded-2xl md:flex-row md:items-center md:justify-between border border-border-color">
            <div className="flex gap-5 items-center">
              <div className="flex justify-center items-center w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-xl">
                <DollarSign className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-base font-semibold text-gray-500 dark:text-gray-400 mb-1">Your Contributions</div>
                {memberContributionStats ? (
                  <>
                    <div className="text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
                      KSh {Number((memberContributionStats?.data?.rent_sum ?? memberContributionStats?.data?.sum) || 0).toLocaleString()}
                    </div>
                    {Number(memberContributionStats?.data?.drive_sum || 0) > 0 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        Drives (separate): KSh {Number(memberContributionStats.data.drive_sum).toLocaleString()}
                      </div>
                    )}
                    <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Total Contributions: {memberContributionStats?.data?.count || 0}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full border-b-2 border-green-600 dark:border-green-400 animate-spin"></div>
                    <span className="text-gray-500 dark:text-gray-400">Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : !isHouseManagement ? (
          // Admin Dashboard - Show group balance with transfer option (hidden for house management)
          <div className="flex flex-col p-4 bg-card-bg rounded-2xl md:flex-row md:items-center md:justify-between border border-border-color">
            <div className="flex gap-5 items-center">
              <div className="flex justify-center items-center w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                <Wallet className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="text-base font-semibold text-gray-500 dark:text-gray-400 mb-1">{getCurrentBalance()}</div>
                {kitBalance ? (
                  <>
                    <div className="text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
                      KSh {Number(kitBalance?.data?.balance || 0).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                      Account No: {kitBalance?.data?.account_no || '-'}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full border-b-2 border-orange-600 dark:border-orange-400 animate-spin"></div>
                    <span className="text-gray-500 dark:text-gray-400">Loading balance...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end mt-8 md:mt-0">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-base px-7 py-3 rounded-xl shadow transition-all duration-200"
                onClick={() => setIsModalOpen(true)}
                disabled={!kitBalance}
              >
                Transfer
              </button>
              {kitBalance?.data?.updated_at ? (
                <div className="mt-4 text-right">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Last updated</p>
                  <p className="text-gray-900 dark:text-white text-lg font-bold">
                    {new Date(kitBalance.data.updated_at).toLocaleString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              ) : (
                <div className="mt-4 text-right">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Last updated</p>
                  <p className="text-gray-900 dark:text-white text-lg font-bold">-</p>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* Member Contributions Table */}
        {!isAdmin && (
          <div className="p-6 bg-card-bg rounded-2xl border border-border-color shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Your Recent Contributions</h3>
              <Link
                to="/contributions"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View All →
              </Link>
            </div>
            
            {isLoadingMemberContributions ? (
              <div className="flex justify-center items-center py-8">
                <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 dark:border-blue-400 animate-spin"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading contributions...</span>
              </div>
            ) : memberContributions?.data?.length > 0 ? (
              <Table
                data={memberContributions.data}
                columns={memberContributionsColumns}
                isLoading={isLoadingMemberContributions}
                showPagination={false}
              />
            ) : (
              <div className="py-8 text-center">
                <DollarSign className="mx-auto mb-4 w-12 h-12 text-gray-300" />
                <h3 className="mb-2 text-lg font-medium text-gray-500">No contributions yet</h3>
                <p className="text-gray-400">Your contribution history will appear here once you start contributing.</p>
              </div>
            )}
          </div>
        )}

        {/* Search and Filter Section */}
        {canViewOthers && (
          <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex flex-col gap-4">
              {/* First Row: Search and Member Filter */}
              <div className="flex flex-col gap-4 md:flex-row">
                {/* Search Input */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search by name, phone, or transaction ID..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
              </div>

              {/* Filters: inline on larger screens, behind one button (bottom sheet) on phones */}
              <PhoneFilters count={(selectedMember ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)}>
                <div className="flex flex-col gap-4 md:flex-row md:items-end">
                {/* Member Filter */}
                <div className="w-full md:w-64">
                  {membersData ? (
                    <Select
                      placeholder="Filter by member..."
                      value={selectedMember}
                      onChange={handleMemberChange}
                      options={memberOptions}
                      isClearable
                      className="w-full"
                      classNamePrefix="select"
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                          boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                          '&:hover': {
                            borderColor: '#3B82F6'
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EFF6FF' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#3B82F6' : '#EFF6FF'
                          }
                        }),
                        menu: (provided) => ({
                          ...provided,
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        })
                      }}
                    />
                  ) : (
                    <div className="flex justify-center items-center h-10 bg-gray-50 rounded-lg border border-gray-300">
                      <div className="w-4 h-4 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading members...</span>
                    </div>
                  )}
                </div>
              <div className="flex flex-col gap-4 md:flex-row md:items-center">
                <div className="flex-1 flex flex-col gap-4 md:flex-row md:items-center">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(1);
                      }}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(1);
                      }}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => {
                        setDateFrom('');
                        setDateTo('');
                        setPage(1);
                      }}
                      className="mt-6 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Clear Dates
                    </button>
                  )}
                </div>
              </div>
                </div>
              </PhoneFilters>
            </div>
          </div>
        )}

        {/* Transactions Table - Only show for users who can view others */}
        {canViewOthers && (
          <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex flex-col px-4 py-3 border-b border-gray-100 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Recent Kit Transactions
                </h3>
                <p className="mt-1 text-sm text-gray-500">Track all your kit activity</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center px-4 py-2 mt-2 text-white bg-green-600 rounded-lg hover:bg-green-700 md:mt-0"
                >
                  <Download className="mr-2 w-4 h-4" />
                  Export
                </button>
              )}
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                <span className="ml-3 text-gray-600">
                  {'Loading transactions...'}
                </span>
              </div>
            ) : (
              <Table
                columns={kitTransactionsColumns}
                data={kitTransactions?.data || []}
                page={page}
                perPage={perPage}
                total={kitTransactions?.meta?.total || 0}
                onPageChange={setPage}
                loading={isLoading}
              />
            )}
          </div>
        )}

        {/* Modal for withdrawing wallet balance */}
        {isModalOpen && (
          <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-40">
            <div className="p-8 w-full max-w-md bg-white rounded-xl shadow-lg">
              <h2 className="mb-4 text-xl font-bold">Transfer from Wallet</h2>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleWithdraw();
                }}
              >
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">Transfer Amount</label>
                  <input
                    type="number"
                    className="px-3 py-2 w-full rounded-lg border"
                    value={newBalance}
                    onChange={e => setNewBalance(e.target.value)}
                    required
                    min="0"
                    max={kitBalance?.data?.balance ?? undefined}
                    placeholder={kitBalance?.data?.balance ? `Max: ${Number(kitBalance.data.balance).toLocaleString()}` : ''}
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">Remarks/Notes</label>
                  <textarea
                    className="px-3 py-2 w-full rounded-lg border"
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    placeholder="e.g. Mary's round payout"
                    rows={3}
                    required
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Transfer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Export Format Modal */}
        {showExportModal && (
          <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
            <div className="p-6 w-full max-w-md bg-white rounded-lg">
              <h2 className="mb-4 text-xl font-bold">Export Format</h2>
              <p className="mb-6 text-gray-600">Choose the format for your transactions export:</p>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('excel')}
                  className="flex justify-between items-center p-4 w-full rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
                  disabled={exportTransactions.isPending}
                >
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-3 w-10 h-10 bg-green-100 rounded-lg">
                      <span className="text-lg font-bold text-green-600">X</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Excel (.xlsx)</div>
                      <div className="text-sm text-gray-500">Best for data analysis and calculations</div>
                    </div>
                  </div>
                  {exportTransactions.isPending && (
                    <div className="w-5 h-5 rounded-full border-b-2 border-green-600 animate-spin"></div>
                  )}
                </button>

                <button
                  onClick={() => handleExport('csv')}
                  className="flex justify-between items-center p-4 w-full rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
                  disabled={exportTransactions.isPending}
                >
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-3 w-10 h-10 bg-blue-100 rounded-lg">
                      <span className="text-sm font-bold text-blue-600">CSV</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">CSV (.csv)</div>
                      <div className="text-sm text-gray-500">Simple text format, compatible with most applications</div>
                    </div>
                  </div>
                  {exportTransactions.isPending && (
                    <div className="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                  )}
                </button>

                <button
                  onClick={() => handleExport('pdf')}
                  className="flex justify-between items-center p-4 w-full rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
                  disabled={exportTransactions.isPending}
                >
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-3 w-10 h-10 bg-red-100 rounded-lg">
                      <span className="text-sm font-bold text-red-600">PDF</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">PDF (.pdf)</div>
                      <div className="text-sm text-gray-500">Professional report format, perfect for sharing</div>
                    </div>
                  </div>
                  {exportTransactions.isPending && (
                    <div className="w-5 h-5 rounded-full border-b-2 border-red-600 animate-spin"></div>
                  )}
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 font-medium text-gray-600 hover:text-gray-800"
                  disabled={exportTransactions.isPending}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Allocation Modal */}
      {syncTx && (
        <div className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl dark:bg-gray-800 p-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              Confirm Allocation
            </h3>
            
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to allocate this payment to the suggested tenant? The tenant will automatically receive an SMS receipt once confirmed.
            </p>

            <div className="p-4 mb-6 space-y-3 bg-gray-50 rounded-lg dark:bg-gray-700/50">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Suggested Member:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {syncTx.suggested_member_name || syncTx.formatted_transaction?.suggested_member_name || 'Member'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone Number:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {syncTx.formatted_transaction?.formatted_account_no}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Reference:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {syncTx.formatted_transaction?.formatted_reference}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Amount:</span>
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                  {syncTx.formatted_transaction?.formatted_amount}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setSyncTx(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                disabled={allocateMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const targetMemberId = syncTx.suggested_member_id || syncTx.formatted_transaction?.suggested_member_id;
                  if (!targetMemberId) {
                    toast.error('Could not find suggested member ID');
                    return;
                  }
                  allocateMutation.mutate({
                    transaction_id: syncTx.id,
                    member_id: targetMemberId
                  });
                }}
                disabled={allocateMutation.isPending}
                className="flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {allocateMutation.isPending ? (
                  <>
                    <svg className="mr-2 w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Allocating...
                  </>
                ) : (
                  'Confirm Allocation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}